import { Add, ContentCopy, Edit } from "@mui/icons-material";
import { Box, Button, Chip, IconButton, Stack, Typography } from "@mui/material";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF, GridRenderCellParams, ValueOptions } from "@mui/x-data-grid-pro";
import { dateColDef } from "components/DataGrid/columnDefs/dateColDef";
import { singleSelectColDef } from "components/DataGrid/columnDefs/singleSelectColDef";
import { DatabaseDataGridSavedFilterTabs } from "components/DataGrid/components/DatabaseDataGridSavedFilterTabs";
import { RevivnDatabaseDataGrid } from "components/DataGrid/RevivnDatabaseDataGrid";
import { RevivnDataGrid } from "components/DataGrid/RevivnDataGrid";
import { CustomDetailPanelToggle, inventoriesColumns } from "components/Orders/columns";
import { PostPackBulkOrderCheckbox } from "hooks/useBulkOrders";
import useCurrentUser from "hooks/useCurrentUser";
import { PagePaperPreamble } from "pages/Layouts/components/PagePaperPreamble";
import { FullPagePaperLayout } from "pages/Layouts/FullPagePaperLayout";
import { useCallback, useEffect, useState } from "react";
import { Outlet, Link as RouterLink, useSearchParams } from "react-router-dom";
import { BulkOrder } from "types/bulkOrder";
import { humanize } from "utils/helpers";
import { userHasPermission } from "utils/userPermissions";
import WeightAndDimsModal from "./BulkOrders/WeightAndDimsModal";
import { PageActions, PageBreadcrumbs, PageTitle } from "./Layouts/components/PageTitleContainer";

const bulkOrdersStateValueOptions: ValueOptions[] = [
  { value: "unpacked", label: "Unpacked" },
  { value: "packed", label: "Ready for invoice" },
  { value: "completed", label: "Completed" },
];

const bulkOrdersStateOptions = {
  unpacked: "#E0E0E0",
  packed: "skyblue",
  completed: "#4CAF5014",
};

export const BulkOrdersPage = () => {
  const { user } = useCurrentUser();
  const url = `bulk_orders`;
  const [showWeightAndDimsModal, setShowWeightAndDimsModal] = useState(false);
  const [currentBulkOrder, setCurrentBulkOrder] = useState<BulkOrder | null>(null);
  const { currentUserPermissions } = useCurrentUser();
  const hasBulkOrderCreatePermission = userHasPermission("bulk_order_create", currentUserPermissions);
  const hasBulkOrderUpdatePermission = userHasPermission("bulk_order_update", currentUserPermissions);
  const [bulkOrderType, setBulkOrderType] = useState<"WholesaleOrder" | "DonationOrder" | null>("WholesaleOrder");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("filter[]")?.includes("type,equals,WholesaleOrder")) {
      setBulkOrderType("WholesaleOrder");
    } else if (searchParams.get("filter[]")?.includes("type,equals,DonationOrder")) {
      setBulkOrderType("DonationOrder");
    } else {
      setBulkOrderType(null);
    }
  }, [searchParams]);

  const getInventoriesContent = useCallback(({ row }: { row: BulkOrder }) => {
    const inventoriesWithIndex = (row.inventories || []).map((inventory, index) => ({
      ...inventory,
      rowNumber: index + 1,
    }));

    return (
      <RevivnDataGrid
        autoHeight
        columns={inventoriesColumns}
        rows={inventoriesWithIndex}
        paginationMode="client"
        pagination
        disableColumnFilter
        disableColumnMenu
        disableColumnResize
        disableColumnPinning
        disableDensitySelector
        disableColumnSelector
        disableSelectionOnClick
        sx={{
          backgroundColor: "#FAFAFA",
        }}
      />
    );
  }, []);

  const bulkOrdersColumns = [
    { field: "customerName", headerName: "Customer Name", width: 250 },
    { field: "manifestId", headerName: "Manifest ID", width: 250 },
    { field: "unitCount", headerName: "Unit Count", width: 100 },
    { field: "warehouseCode", headerName: "Warehouse Code", width: 150 },
    { field: "shippingMethod", headerName: "Shipping Method", width: 150 },
    { field: "dueDate", headerName: "Due Date", width: 100, ...dateColDef },
    {
      field: "weightAndDims",
      headerName: "Weight & Dims",
      width: 200,
      filterable: false,
      sortable: false,
      type: "text",
      renderCell: (params: GridRenderCellParams<BulkOrder>) => (
        <Box
          sx={{
            py: 1,
            display: "flex",
            alignItems: "flex-start",
            gap: 0.5,
            maxHeight: "100%",
            width: "100%",
            overflowY: "scroll",
          }}
        >
          <Box sx={{ flex: 1, maxHeight: "100%" }}>
            <Typography sx={{ whiteSpace: "pre-line" }}>{params.row.weightAndDims || "-"}</Typography>
          </Box>
          {params.row.weightAndDims && (
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText(params.row.weightAndDims)}
              title="Copy to clipboard"
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
    {
      field: "id",
      headerName: "Post-Pack",
      width: 160,
      filterable: false,
      hideable: false,
      renderCell: (row: GridRenderCellParams<BulkOrder>) => <PostPackBulkOrderCheckbox bulkOrder={row.row} />,
    },
    {
      field: "state",
      headerName: "State",
      width: 150,
      ...singleSelectColDef(bulkOrdersStateValueOptions),
      renderCell: (params: GridRenderCellParams<BulkOrder>) => {
        return (
          <Chip
            sx={{ backgroundColor: bulkOrdersStateOptions[params.row.state as keyof typeof bulkOrdersStateOptions] }}
            label={humanize(params.row.state).replace(/ /g, "_")}
          />
        );
      },
    },
    { field: "type", headerName: "Type", width: 150 },
    {
      field: "editActions",
      headerName: "",
      filterable: false,
      hideable: false,
      renderCell: (row: GridRenderCellParams<BulkOrder>) => (
        <Stack direction="row" gap={0.5}>
          <IconButton
            onClick={() => {
              setCurrentBulkOrder(row.row);
              setShowWeightAndDimsModal(true);
            }}
            disabled={!hasBulkOrderUpdatePermission}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      hideable: false,
      renderCell: (params: GridRenderCellParams<BulkOrder>) => <CustomDetailPanelToggle {...params} />,
    },
  ];

  if (!user) return null;
  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Fulfillment" }]} />
      <PageTitle>Bulk Orders</PageTitle>
      <PagePaperPreamble>
        <DatabaseDataGridSavedFilterTabs
          url={url}
          savedFilters={[
            {
              label: "Wholesale Orders",
              filters: "type,equals,WholesaleOrder AND state,not,completed",
            },
            {
              label: "Donation Orders",
              filters: "type,equals,DonationOrder AND state,not,completed",
            },
            {
              label: "All Bulk Orders",
              filters: "__any__",
            },
          ]}
        />
      </PagePaperPreamble>
      <PageActions>
        {bulkOrderType && (
          <Button
            component={RouterLink}
            startIcon={<Add />}
            to={`new?type=${bulkOrderType}&filter[]=type,equals,${bulkOrderType}&filter[]=state,not,completed`}
            variant="contained"
            sx={{ display: hasBulkOrderCreatePermission ? "" : "none" }}
          >
            Add {bulkOrderType === "DonationOrder" ? "Donation" : "Wholesale"} Order
          </Button>
        )}
      </PageActions>

      <WeightAndDimsModal
        bulkOrder={currentBulkOrder}
        open={showWeightAndDimsModal}
        onClose={() => setShowWeightAndDimsModal(false)}
      />

      <RevivnDatabaseDataGrid
        url={url}
        syncStateWithSearchParams
        remoteCsvExport={true}
        persistSettingsKey="bulkOrdersPage"
        columns={bulkOrdersColumns}
        autoHeight={false}
        getDetailPanelContent={getInventoriesContent}
        getDetailPanelHeight={() => "auto"}
        initialState={{
          columns: {
            columnVisibilityModel: {
              type: false,
            },
          },
        }}
        disableSelectionOnClick
      />
      <Outlet />
    </FullPagePaperLayout>
  );
};
